import type { SSEError, SSEStreamCallbacks } from "@/hooks/useApi";
import {
  applyCompanyPatch,
  type CompanyDTO,
  type CompanySectionKey,
  type CompanySectionStatus,
  type CompanyStreamEvent,
} from "@shaarp/common";

/** What the company page stream has sent so far for one company. */
export type CompanyPageState = {
  /** The stored row from the snapshot, then patched by each section. */
  company: CompanyDTO | null;
  sections: Partial<Record<CompanySectionKey, CompanySectionStatus>>;
  /** The run is over: `done` arrived, or the stream failed or closed. */
  done: boolean;
  /** The team's enrichment budget is spent: the page shows the stored row. */
  budgetSpent: boolean;
  /** Why the run ended early: a refusal carries the API's `status` and `code`. */
  error: SSEError | null;
  /**
   * `company` may be older than the row: the run that painted it ended
   * before `done` — its stream failed, closed or was refused, or its last
   * view left — while the server's run may have gone on, and no snapshot has
   * replaced it since (a new run carries it until its own arrives). The page
   * still paints it; nothing else takes it for the company's latest data.
   */
  isCompanyBehind: boolean;
  /**
   * Its last view left before the run ended: the stream was cut while the
   * server's run went on. Only a new run of the page reads it again; every
   * other view reads it as no run at all.
   */
  isAbandoned: boolean;
};

/** The `get` of `useApiStream()`, handed in so the headers stay the hook's. */
export type CompanyPageGet = (
  uri: string,
  callbacks: SSEStreamCallbacks,
) => Promise<void>;

export const INITIAL_COMPANY_PAGE_STATE: CompanyPageState = {
  company: null,
  sections: {},
  done: false,
  budgetSpent: false,
  error: null,
  isCompanyBehind: false,
  isAbandoned: false,
};

// Session-long cache: bounded so a long session cannot grow it without limit.
const MAX_ENTRIES = 50;

/**
 * `sections` with every section still `pending` settled as `status`: once the
 * run is over no event will settle it, and a card waiting on it would wait
 * forever.
 */
function settlePending(
  sections: CompanyPageState["sections"],
  status: CompanySectionStatus,
): CompanyPageState["sections"] {
  return Object.fromEntries(
    Object.entries(sections).map(([key, current]) => [
      key,
      current === "pending" ? status : current,
    ]),
  );
}

/**
 * `state` once its run ended before `done`: the company already painted stays,
 * behind the row, and a section still pending settles `error`.
 */
function endEarly(state: CompanyPageState): CompanyPageState {
  return {
    ...state,
    done: true,
    sections: settlePending(state.sections, "error"),
    isCompanyBehind: true,
  };
}

/**
 * The state after one event of the stream. A `section` replaces the top-level
 * keys of its patch — also for a section the snapshot marked `fresh`, which a
 * viewer following another run can receive — and records its status. `done`
 * ends the run; a section it left pending (a spent budget sends none) settles
 * `empty`.
 */
export function applyCompanyStreamEvent(
  state: CompanyPageState,
  event: CompanyStreamEvent,
): CompanyPageState {
  switch (event.type) {
    case "snapshot":
      return {
        ...state,
        company: event.company,
        sections: event.sections,
        isCompanyBehind: false,
      };
    case "section":
      return {
        ...state,
        company: state.company && applyCompanyPatch(state.company, event.patch),
        sections: { ...state.sections, [event.key]: event.status },
      };
    case "done":
      return {
        ...state,
        done: true,
        budgetSpent: event.budgetSpent,
        sections: settlePending(state.sections, "empty"),
      };
    default:
      return state;
  }
}

/**
 * How far the run is, 0 to 100: the share of the snapshot's sections that
 * settled, and 100 once the run is over.
 */
export function companyPageProgress(state: CompanyPageState): number {
  if (state.done) return 100;
  const statuses = Object.values(state.sections);
  if (statuses.length === 0) return 0;
  const settled = statuses.filter((status) => status !== "pending").length;
  return Math.round((settled / statuses.length) * 100);
}

const EVENT_TYPES: ReadonlySet<string> = new Set(["snapshot", "section", "done"]);

function isStreamEvent(value: unknown): value is CompanyStreamEvent {
  return (
    typeof value === "object" &&
    value !== null &&
    EVENT_TYPES.has(String((value as { type?: unknown }).type))
  );
}

function parseEvent(data: string): CompanyStreamEvent | null {
  try {
    const parsed: unknown = JSON.parse(data);
    return isStreamEvent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

type Run = { token: object; abort: AbortController | null };

/**
 * The entry of `companyId` as `teamId` sees it. The stream answers with the
 * team's view of the company (its notes, the key contacts it may see), and a
 * team switch does not always reload the page: without the team, reopening a
 * company would join, or show, the previous team's run.
 */
function entryKey(teamId: string, companyId: string): string {
  return `team:${teamId}|${companyId}`;
}

const states = new Map<string, CompanyPageState>();
/** The run each entry listens to; an older one's late events are dropped. */
const runs = new Map<string, Run>();
/** How many mounted views show each entry (`retain`). */
const views = new Map<string, number>();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

/** Aborts the stream `key` listens to, if it still runs, and forgets it. */
function stopRun(key: string): void {
  runs.get(key)?.abort?.abort();
  runs.delete(key);
}

/** Forgets `key`, aborting its stream if it still runs. */
function drop(key: string): void {
  stopRun(key);
  states.delete(key);
}

/**
 * Drops the oldest entry once full — a finished one first, the oldest running
 * one only when none has finished. Its subscribers are told, so a page still
 * showing it starts it again rather than waiting on an entry that is gone.
 */
function evictIfFull(): void {
  if (states.size < MAX_ENTRIES) return;
  const oldest =
    [...states].find(([, state]) => state.done)?.[0] ??
    states.keys().next().value;
  if (oldest === undefined) return;
  drop(oldest);
  emit();
}

/**
 * Ends the run of `key` once no view shows it: its stream is aborted — an open
 * SSE holds one of the browser's few connections to the API for as long as the
 * server's run lasts — and the entry ends, abandoned, with the company it
 * painted, so the next `start` opens a new run instead of joining a dead one.
 * The server's run carries on and is written either way.
 */
function endUnviewed(key: string): void {
  const state = states.get(key);
  if (!state || state.done) return;
  stopRun(key);
  states.set(key, { ...endEarly(state), isAbandoned: true });
  emit();
}

/**
 * One run of `GET /company/:id`, from the request to its end. Only the latest
 * run of an entry writes to it: a replaced run is aborted, and its late events
 * — a stream may still deliver after its abort — are dropped. Every way a run
 * ends sets `done`: the `done` event, an `event: error` (NestJS sends a
 * server-side failure as a message that is not an event), a close without
 * `done`, a refusal or a network error. The company already painted stays; a
 * section still pending settles `error`.
 */
async function run(
  teamId: string,
  companyId: string,
  get: CompanyPageGet,
): Promise<void> {
  const key = entryKey(teamId, companyId);
  const previous = states.get(key);
  stopRun(key);
  // An entry already held is replaced, so nothing to make room for; it moves
  // last, so the first evicted is the one started longest ago.
  if (previous) states.delete(key);
  else evictIfFull();
  const current: Run = { token: {}, abort: null };
  runs.set(key, current);
  let hasEnded = false;
  // The company stays painted until the new snapshot replaces it, behind the
  // row still when its run ended early.
  states.set(key, {
    ...INITIAL_COMPANY_PAGE_STATE,
    company: previous?.company ?? null,
    isCompanyBehind: previous?.isCompanyBehind ?? false,
  });
  emit();

  const isLive = () => runs.get(key) === current && !hasEnded;
  const update = (change: (state: CompanyPageState) => CompanyPageState) => {
    const state = states.get(key);
    if (!state || !isLive()) return;
    states.set(key, change(state));
    emit();
  };
  const fail = (error: SSEError) => {
    update((state) => ({ ...endEarly(state), error }));
    hasEnded = true;
  };

  await get(`/company/${encodeURIComponent(companyId)}`, {
    onOpen: (abort) => {
      if (runs.get(key) === current) current.abort = abort;
      else abort.abort();
    },
    onMessage: ({ data }) => {
      if (!isLive() || !data) return;
      const event = parseEvent(data);
      if (!event) {
        fail(new Error("The company stream sent an error event"));
        return;
      }
      update((state) => applyCompanyStreamEvent(state, event));
      if (event.type === "done") hasEnded = true;
    },
    onClose: () => {
      if (!isLive()) return;
      fail(new Error("The company stream closed before it was done"));
    },
    // A refusal before streaming carries the API's `status` and `code`
    // (`NO_LICENCE`…); the throttler's 429 carries a status only.
    onError: (error) => {
      if (!isLive()) return;
      fail(error as SSEError);
    },
  });
}

/**
 * Module-level store of the company page stream, keyed by team and company:
 * one run per entry, shared by everything that shows it — the page, the side
 * panel, the table rows that read its latest data. `start` joins the run in
 * progress, or runs a finished company again so the page reads the row anew;
 * `refresh` replaces the run in progress. A page `retain`s the entry while it
 * is mounted: once the last view releases it, its stream is aborted. A run
 * nothing retains (the side panel's enrichment) runs to its end.
 */
export const companyPageStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getState(teamId: string, companyId: string): CompanyPageState | undefined {
    return states.get(entryKey(teamId, companyId));
  },
  async start(
    teamId: string,
    companyId: string,
    get: CompanyPageGet,
  ): Promise<void> {
    const existing = states.get(entryKey(teamId, companyId));
    if (existing && !existing.done) return;
    await run(teamId, companyId, get);
  },
  async refresh(
    teamId: string,
    companyId: string,
    get: CompanyPageGet,
  ): Promise<void> {
    await run(teamId, companyId, get);
  },
  /** Counts a view of the entry; the function returned releases it, once. */
  retain(teamId: string, companyId: string): () => void {
    const key = entryKey(teamId, companyId);
    views.set(key, (views.get(key) ?? 0) + 1);
    let isReleased = false;
    return () => {
      if (isReleased) return;
      isReleased = true;
      const remaining = (views.get(key) ?? 1) - 1;
      if (remaining > 0) {
        views.set(key, remaining);
        return;
      }
      views.delete(key);
      endUnviewed(key);
    };
  },
};
